import DialogueFooter from 'src/components/GenericDialogueFooter/GenericDialogueFooter';
import GenericDialogue from 'src/components/Dialogue/Dialogue';
import DialogueBody from 'src/components/GenericDialogueBody/GenericDialogueBody';
import {
  useUnitImport,
  UseUnitImportReturn,
} from 'src/pages/Suite/Components/UnitImportDialogue/Hooks/useUnitImport';
import {
  MANDATORY_UNIT_COLUMNS,
  OPTIONAL_UNIT_COLUMNS,
  ALL_UNIT_COLUMNS,
} from 'src/pages/Suite/Components/UnitImportDialogue/Hooks/Helpers/config';
import RowValidationErrorCard from 'src/pages/Property/HomeOwnerImportDialogue/Components/RowValidationCard/RowValidationCard';
import CsvTemplateDownloadCard from 'src/components/CsvTemplateDownloadCard/CsvTemplateDownloadCard';
import { useDropzone } from 'react-dropzone';
import { useCallback } from 'react';
import UnitImportPreview from 'src/pages/Suite/Components/UnitImportDialogue/UnitImportPreveiw';
import './UnitImportDialogue.scss';
import AssetCatalogueMismatchCard from 'src/pages/Suite/Components/UnitImportDialogue/AssetCatalogueMismatchCard';
import ParkingPermissionErrorCard from 'src/pages/Suite/Components/UnitImportDialogue/ParkingPermissionErrorCard';

interface UnitImportDialogueProps {
  isOpen: boolean;
  onClose: () => void;
  onImportComplete: () => void;
  propertyId: string;
  userId: string;
}

const UnitImportDialogue = ({
  isOpen,
  onClose,
  onImportComplete,
  propertyId,
  userId,
}: UnitImportDialogueProps) => {
  const {
    file,
    validationStatus,
    columnValidation,
    optionalMissingColumns,
    parsedRecords,
    rowValidationErrors,
    rowValidationWarnings,
    importStatus,
    importResult,
    apiError,
    catalogueMismatch,
    parkingPermissionError,
    processFile,
    runImport,
    reset,
  }: UseUnitImportReturn = useUnitImport(propertyId, userId);

  const { getRootProps, getInputProps, open } = useDropzone({
    noClick: true,
    accept: '.csv',
    multiple: false,
    onDrop: (acceptedFiles: File[]) => {
      if (acceptedFiles[0]) {
        processFile(acceptedFiles[0]);
      }
    },
  });

  const handleClose = useCallback(() => {
    reset();
    onClose();
  }, [reset, onClose]);

  const handleImportComplete = useCallback(() => {
    reset();
    onImportComplete();
    onClose();
  }, [reset, onImportComplete, onClose]);

  const isImporting: boolean = importStatus === 'importing';
  const isSuccess: boolean = importStatus === 'success';
  const isImportError: boolean = importStatus === 'error';
  const isValidCsv: boolean = validationStatus === 'valid';
  const isImportEnabled: boolean = isValidCsv && !isImporting;

  return (
    <GenericDialogue
      isOpen={isOpen}
      onClose={handleClose}
      title="Import Units"
      description="Upload a CSV to bulk-import or update units in this property. Only Number and Building are required — include additional columns to update specific fields."
      maxWidth="1100px"
      footer={
        <DialogueFooter
          isSuccess={isSuccess}
          isImporting={isImporting}
          file={file}
          isValidCsv={isValidCsv}
          isImportError={isImportError}
          isImportEnabled={isImportEnabled}
          importButtonLabel="Import Units"
          reset={reset}
          handleImportComplete={handleImportComplete}
          handleClose={handleClose}
          runImport={runImport}
        />
      }
    >
      <div className="uid_body">
        {!file && !isSuccess && (
          <CsvTemplateDownloadCard
            filename="unit-import-template.csv"
            columns={ALL_UNIT_COLUMNS}
          />
        )}
        <DialogueBody
          isSuccess={isSuccess}
          isImporting={isImporting}
          file={file}
          importResult={importResult}
          recordCount={parsedRecords.length}
          validationStatus={validationStatus}
          columnValidation={columnValidation}
          hasRowErrors={rowValidationErrors.length > 0}
          isImportError={isImportError}
          apiError={apiError}
          entityName="unit"
          requiredColumns={MANDATORY_UNIT_COLUMNS}
          optionalColumns={OPTIONAL_UNIT_COLUMNS}
          optionalMissingColumns={optionalMissingColumns}
          getRootProps={getRootProps}
          getInputProps={getInputProps}
          open={open}
          reset={reset}
        >
          {rowValidationErrors.length > 0 && (
            <RowValidationErrorCard
              errors={rowValidationErrors}
              variant="error"
            />
          )}
          {rowValidationWarnings.length > 0 && (
            <RowValidationErrorCard
              errors={rowValidationWarnings}
              variant="warning"
            />
          )}
          {validationStatus === 'valid' && (
            <UnitImportPreview records={parsedRecords} />
          )}
          {catalogueMismatch && (
            <AssetCatalogueMismatchCard mismatch={catalogueMismatch} />
          )}
          {parkingPermissionError && (
            <ParkingPermissionErrorCard
              permissionError={parkingPermissionError}
            />
          )}
        </DialogueBody>
      </div>
    </GenericDialogue>
  );
};

export default UnitImportDialogue;